import React from "react";
import { LegacyCard, LegacyStack, RadioButton, List, Tooltip, InlineStack } from "@shopify/polaris";
import { HelpIconLink } from "../../common/HelpIconLink";

export function InventorySyncOptions({
  userSyncOption,
  handleSyncOptionChange,
  shopifyShops,
}: {
  readonly userSyncOption: string;
  readonly handleSyncOptionChange: (newValue: boolean, id: string) => void;
  readonly shopifyShops: { shopify_domain: string }[];
}): React.JSX.Element {
  const basicSyncDisabled = shopifyShops.length > 1;
  let basicSyncRadioButton = (
    <RadioButton
      checked={userSyncOption === "hybrid_sync"}
      disabled={basicSyncDisabled}
      helpText={
        <List type="bullet">
          <List.Item>Shopify and Horse share inventory responsibility.</List.Item>
          <List.Item>
            Inventory changes made in Shopify will be synced to Horse and inventory changes made in Horse will be synced
            to Shopify.
          </List.Item>
        </List>
      }
      id="hybrid_sync"
      label="Basic Sync"
      onChange={handleSyncOptionChange}
    />
  );

  if (basicSyncDisabled) {
    basicSyncRadioButton = (
      <Tooltip content="Basic sync is not available when multiple stores are connected">{basicSyncRadioButton}</Tooltip>
    );
  }

  const title = (
    <InlineStack gap="100">
      Select sync mode
      <HelpIconLink url="https://horse-inventory.notion.site/Settings-49969145267e46de9d336d62994312d0?pvs=97#e67869f842c240fc9be1b53647260308" />
    </InlineStack>
  );

  return (
    <LegacyCard>
      <LegacyCard.Header title={title} />
      <LegacyCard.Section>
        <List type="bullet">
          <List.Item>
            Variants are always linked together based on their SKU and barcode. To unlink variants, simply change their
            SKU or barcode in your Shopify store.
          </List.Item>
          <List.Item>Locations are always linked together based on their name.</List.Item>
          <List.Item>Changes that you make to your inventory in Horse will always be synced to Shopify.</List.Item>
        </List>
      </LegacyCard.Section>
      <LegacyCard.Section>
        <LegacyStack vertical>
          {basicSyncRadioButton}
          <RadioButton
            checked={userSyncOption === "full_sync"}
            helpText={
              <List type="bullet">
                <List.Item>
                  Horse is the master of all inventory quantities. Horse will ignore any inventory changes made in
                  Shopify.
                </List.Item>
                <List.Item>Inventory changes from any store will be synced to all other stores.</List.Item>
                <List.Item>
                  Inventory changes to any linked variant will be made to all other linked variants.
                </List.Item>
              </List>
            }
            id="full_sync"
            label="Full Sync"
            onChange={handleSyncOptionChange}
          />
        </LegacyStack>
      </LegacyCard.Section>
    </LegacyCard>
  );
}
